<template>
        <modal-side-detail-mdw-line-group
        subTitleIcon="arrow-down-full"
        forceNegativeAmount
        :headerColor="headerColor"
        :title="detailData.title"
        :subTitle="detailSubTitle"
        :icon="detailData.status.icon"
        :buttonsTopData="detailData.hasOptions ? buttonsTopData : null"
        :requestPayload="{ operationId: detailData.id }"
        :errorStateMessage="t('transfers.history.international.modal.detail.emptyStates.errorStateMessage')"
        :detailRequestAction="detailData.requestAction"
        :statusIconSvgName="detailData.status.icon"
        :statusIconColor="detailData.status.color"
        :showBottomContent="detailData.favorite || detailData.favoritable"
        @hideMe="emit('hideMe')">
            <template v-if="detailData.favoritable" v-slot:bottomContent>
                <div class="favorite-button-wrapper">
                    <button-tertiary
                    :key="favoriteButtonOptions.icon"
                    :label="favoriteButtonOptions.label"
                    :icon="favoriteButtonOptions.icon"
                    @clicked="showFavoritesModal = true" />
                </div>
            </template>
        </modal-side-detail-mdw-line-group>
        <!-- Favorites modal -->
        <favorites-modal
        v-if="showFavoritesModal"
        :modalParams="{ operationId: detailData.id, type: favoriteButtonOptions.type }"
        rightButtonIcon="check-line"
        @hideMe="showFavoritesModal = false"
        @requestSuccessful="favoriteModalSuccessHandler"
        @requestError="favoriteModalErrorHandler" />
        <!-- Feedback -->
        <toast-generic
        v-if="showFeedbackToast"
        :toastType="feedbackToastData.toastType"
        :feedbackText="feedbackToastData.text"
        @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import ModalSideDetailMdwLineGroup from '@/components/generic/modals/side/DetailMdwLineGroup'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import FavoritesModal from '@/components/generic/modals/centered/Favorite'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed } from 'vue'

/* Store */
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'

/* Aux */
import { amountFormat } from '@/assets/scripts/amounts'
import { errorObjectConstructor, sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'updateTransferFavoriteStatus'])

/* Props
@detailData { Object } - object with transfer details
*/
const props = defineProps({
    detailData: Object
})

/* Data */
const showFavoritesModal = ref(false)

const buttonsTopData = ref({
    downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: props.detailData.id } }),
    emailAction: 'sendOperationEmailNotification',
    smsAction: 'sendOperationSMSNotification',
    sendPayload: { operationId: props.detailData.id },
    operationId: props.detailData.id
})

const headerColor = computed(() => {
    return `var(--${props.detailData.status.color})`
})

const detailSubTitle = computed(() => {
    if (props.detailData.amount.content) {
        return amountFormat(Math.abs(props.detailData.amount.content), props.detailData.amount.currency)
    } else {
        return null
    }
})

const favoriteButtonOptions = computed(() => {
    if (props.detailData.favorite) {
        return {
            label: t('transfers.history.international.modal.detail.buttons.removeFavorite'),
            icon: 'star-minus',
            type: 'remove'
        }
    } else {
        return {
            label: t('transfers.history.international.modal.detail.buttons.saveAsFavorite'),
            icon: 'star',
            type: 'create'
        }
    }
})

/*************************************************
*                                                *
*                 Favorite Modal                 *
*                                                *
*************************************************/
const favoriteModalSuccessHandler = (ev) => {
    emit('updateTransferFavoriteStatus', {
        favoriteId: props.detailData.id,
        isFavorite: favoriteButtonOptions.value.type === 'create',
        isFavoritable: favoriteButtonOptions.value.type === 'remove'
    })

    sucessFeedbackToastHandler(ev)
    showFavoritesModal.value = false
}

const favoriteModalErrorHandler = (ev) => {
    errorFeedbackToastHandler(ev)
    showFavoritesModal.value = false
}

/*************************************************
*                                                *
*               Feedback Toast Logic             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorFeedbackToastHandler = (data, retryFn) => {
    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

const sucessFeedbackToastHandler = data => {
    feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
}
</script>

<style lang="scss" scoped>
</style>
